/**
 * FlexibleHeaderList - Full-page layout with collapsing header as appbar (Flutter equivalent SliverAppBar)
 * and virtual list or grid as content.
 *
 * Web equivalent of Flutter's GridWidget (grid_widget.dart) combined with:
 * - SliverAppBar (collapsing sticky title bar)
 * - FlexibleSpaceBar with CollapseMode.parallax (parallax background)
 * - SliverPersistentHeader(pinned: true) (optional sticky sub-header)
 * - SliverGrid or SliverList via VirtualList (grid mode when gridProps is passed, list mode otherwise)
 *
 * Props mirror Flutter's GridOptionsAppBarOptions and GridFlexibleSpaceOptions
 * where there is a web-viable equivalent.
 */

import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import type { VirtualListProps, VirtualListHandle, GridProps } from '@/presentation/components/common/virtualList/VirtualList';
export type { GridProps } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

// ─── FlexibleSpaceOptions ────────────────────────────────────────────────────

/**
 * Configures the collapsing flexible space below the title bar.
 * Web equivalent of Flutter's GridFlexibleSpaceOptions.
 */
export interface FlexibleSpaceOptions {
  /**
   * Fully custom background element rendered behind the flexible space.
   * When provided, `centerWidget` and `backgroundStyle` are ignored.
   * Equivalent to GridFlexibleSpaceOptions.flexibleBackground.
   */
  background?: ReactNode;

  /**
   * Widget centered in the default background (used when `background` is null).
   * Equivalent to GridFlexibleSpaceOptions.centerWidget.
   */
  centerWidget?: ReactNode;

  /**
   * Size (width & height) in px of the centerWidget container. Default: 80.
   * Equivalent to GridFlexibleSpaceOptions.centerWidgetSize.
   */
  centerWidgetSize?: number;

  /**
   * CSS styles applied to the default background container.
   * Equivalent to GridFlexibleSpaceOptions.backgroundDecoration.
   */
  backgroundStyle?: CSSProperties;

  /**
   * Enable parallax scroll effect on the background. Default: true.
   * Equivalent to Flutter's CollapseMode.parallax — background scrolls at ~40% speed.
   */
  parallax?: boolean;

  /** Padding above the background content. Default: responsive.gap.lg. */
  topPadding?: number;

  /** Padding below the background content. Default: responsive.gap['3xl']. */
  bottomPadding?: number;
}

// ─── AppBarOptions ────────────────────────────────────────────────────────────

/**
 * Configures the sticky title bar and its optional collapsing flexible space.
 * Web equivalent of Flutter's GridOptionsAppBarOptions.
 */
export interface AppBarOptions {
  /** Title text centered in the title bar. */
  title?: string;

  /**
   * Extra widget rendered after the title (e.g. a verified badge).
   * Equivalent to GridOptionsAppBarOptions.titleExtra.
   */
  titleExtra?: ReactNode;

  /**
   * Custom leading widget (left of the title bar).
   * Defaults to a BackIconButton.
   * Equivalent to GridOptionsAppBarOptions.leadingWidget.
   */
  leadingWidget?: ReactNode;

  /**
   * Tap handler for the default leading back button.
   * Ignored when `leadingWidget` is provided.
   */
  onLeadingTap?: () => void;

  /**
   * When true, no leading widget is rendered (no back button, no custom widget).
   * Takes precedence over `leadingWidget`.
   */
  hideLeading?: boolean;

  /**
   * Action widgets rendered on the right side of the title bar.
   * Equivalent to GridOptionsAppBarOptions.actions.
   */
  actions?: ReactNode;

  /**
   * Background color of the title bar.
   * Blended with a dynamic alpha: nearly transparent when expanded, ~50% when fully collapsed.
   * Equivalent to GridOptionsAppBarOptions.backgroundColor.
   */
  backgroundColor?: string;

  /**
   * Collapsing flexible space below the title bar with optional parallax.
   * When null, the header renders only the title bar (height: titleBarHeight).
   * Equivalent to GridOptionsAppBarOptions.videosGridFlexibleSpaceOptions.
   */
  flexibleSpace?: FlexibleSpaceOptions;

  /**
   * When true, the title is always fully visible (opacity 1) instead of
   * fading in as the flexibleSpace collapses. Default: false.
   */
  alwaysShowTitle?: boolean;
}

// ─── Handle ───────────────────────────────────────────────────────────────────

export interface FlexibleHeaderListHandle {
  /** Scroll to a specific item index. */
  scrollToIndex: (index: number, behavior?: ScrollBehavior) => void;
}

// ─── Sentinel type for the addItemComponent slot ──────────────────────────────

type AddItemSentinel = { readonly __flexListSentinel: 'add' };
const ADD_ITEM_SENTINEL: AddItemSentinel = { __flexListSentinel: 'add' };

function isAddItemSentinel(item: unknown): item is AddItemSentinel {
  return (
    typeof item === 'object' &&
    item !== null &&
    '__flexListSentinel' in item &&
    (item as AddItemSentinel).__flexListSentinel === 'add'
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FlexibleHeaderListProps<T> {
  // ── VirtualList ──────────────────────────────────────────────────────────────

  /** Items to render. */
  items: T[];

  /** Render function for each item. */
  renderItem: (item: T, index: number) => ReactNode;

  /** Unique key extractor per item. */
  getItemKey: (item: T, index: number) => string | number;

  /**
   * Grid layout configuration. When provided, renders as a virtualized grid.
   * When absent, renders as a virtualized list.
   */
  gridProps?: GridProps;

  /** Initial item index to scroll to after mount. */
  initialIndex?: number;

  /**
   * Chat-style bottom-anchored scrolling: the list pins to the bottom and
   * `onLoadMore` fires near the top (load-more-older edge). Forwarded verbatim
   * to the inner {@link VirtualList} — list mode only; ignored (with a dev-mode
   * warning) when `gridProps` is set. Default: `false`.
   *
   * Note: a reversed page that scrolls the window also needs the bottom-pin
   * viewport inset (`reversedBottomPinInset`) wired through to account for the
   * sticky app bar / persistent header; this passthrough does not surface that.
   */
  reversed?: boolean;

  /** Called when the visible / rendered item range changes. */
  onVisibleItemsChange?: VirtualListProps<T>['onVisibleItemsChange'];

  // ── Load more ───────────────────────────────────────────────────────────────

  /**
   * Called when the user scrolls within 200px of the bottom sentinel.
   * Equivalent to GridWidget.onLoadMoreMedias.
   */
  onLoadMore?: () => void;

  /** Whether more items are available. Controls visibility of the sentinel. */
  hasMore?: boolean;

  /** Whether a load-more request is currently in flight. Shows a loading indicator. */
  isLoadingMore?: boolean;

  // ── Empty state ─────────────────────────────────────────────────────────────

  /**
   * Rendered when `items` is empty and a first-fetch error occurred.
   * Takes priority over `emptyListComponent` when provided.
   */
  errorOnFirstFetchComponent?: ReactNode;

  /**
   * Rendered when `items` is empty.
   * Shown alongside the list (which may contain just `addItemComponent`),
   * matching Flutter's GridWidget.emptyListWidget behavior.
   * Defaults to a centered "No data" text.
   */
  emptyListComponent?: ReactNode;

  // ── Add-item slot ───────────────────────────────────────────────────────────

  /**
   * Rendered as the first cell of the grid.
   * Equivalent to GridWidget.newItemtWidget (e.g. an "Add video" button).
   * When null, no extra slot is prepended.
   */
  addItemComponent?: ReactNode;

  // ── Header ──────────────────────────────────────────────────────────────────

  /**
   * Title bar and optional collapsing flexible space configuration.
   * When null, no header is rendered.
   */
  appBarOptions?: AppBarOptions;

  /**
   * Content pinned below the app bar as the user scrolls (e.g. a tab bar).
   * Equivalent to Flutter's SliverPersistentHeader(pinned: true).
   */
  persistentHeader?: ReactNode;

  /**
   * Height in pixels of the persistent header, required for correct sticky offset.
   * Default: 48.
   */
  persistentHeaderHeight?: number;

  // ── Container styling ───────────────────────────────────────────────────────

  /** Background color of the whole page container. */
  backgroundColor?: string;

  /** Additional CSS class for the page container. */
  className?: string;

  /** Additional inline style for the page container. */
  style?: CSSProperties;

  // ── QA test attributes ──────────────────────────────────────────────────────

  /**
   * Optional `data-qa-*` attributes spread onto the page root container, so the
   * `qa-tester` agent can name the page and read a coarse lifecycle/status token.
   * Additive and inert; never affects rendering.
   */
  qa?: QaAttrOptions;

  /**
   * Optional `data-qa-*` attributes forwarded to the inner {@link VirtualList}
   * root, so the content list can be named independently of the page root.
   * Additive and inert; never affects rendering.
   */
  listQa?: QaAttrOptions;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FlexibleHeaderList = forwardRef(<T,>(
  {
    items,
    renderItem,
    getItemKey,
    gridProps,
    initialIndex,
    reversed,
    onVisibleItemsChange,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    errorOnFirstFetchComponent,
    emptyListComponent,
    addItemComponent,
    appBarOptions,
    persistentHeader,
    persistentHeaderHeight = 48,
    backgroundColor,
    className,
    style,
    qa,
    listQa,
  }: FlexibleHeaderListProps<T>,
  ref: React.Ref<FlexibleHeaderListHandle>
) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const titleBarHeight = responsive.topBar.height;
  const [scrollY, setScrollY] = useState(0);
  const [measuredExpandedHeight, setMeasuredExpandedHeight] = useState(0);
  const flexibleSpaceRef = useRef<HTMLDivElement>(null);
  const virtualListRef = useRef<VirtualListHandle>(null);

  // Expose scrollToIndex imperatively
  useImperativeHandle(ref, () => ({
    scrollToIndex: (index, behavior) =>
      virtualListRef.current?.scrollToIndex(index, behavior),
  }), []);

  // Track window scroll for the parallax / collapse effect.
  // Only attach listener when a flexible space is configured.
  useEffect(() => {
    if (!appBarOptions?.flexibleSpace) return;
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [appBarOptions?.flexibleSpace]);

  // ── Header measurements ────────────────────────────────────────────────────

  const hasFlexibleSpace = !!appBarOptions?.flexibleSpace;

  // Measure the flexible space height from the DOM so callers don't need to
  // specify expandedHeight manually. useLayoutEffect fires synchronously
  // before the first browser paint, so measuredExpandedHeight is always set
  // before the user ever sees the page. ResizeObserver keeps it accurate if
  // content changes (e.g. dynamic profile data arriving).
  useLayoutEffect(() => {
    if (!hasFlexibleSpace) return;
    const el = flexibleSpaceRef.current;
    if (!el) return;
    const measure = () => setMeasuredExpandedHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasFlexibleSpace]);

  // 0 = fully expanded, 1 = fully collapsed.
  // collapseRange is the scroll distance over which the flexible space
  // transitions from fully visible to fully behind the appbar.
  const collapseRange = Math.max(1, measuredExpandedHeight - titleBarHeight);
  const collapseProgress = hasFlexibleSpace
    ? Math.min(1, scrollY / collapseRange)
    : 1;

  // Parallax: background translates downward at 55% of scroll speed, so it
  // appears to scroll slower than the page content. The gap this creates at
  // the top of the container is always scrolled above the viewport (page y=0
  // to parallaxOffset is at viewport y ≤ -scrollY*(1-0.55) < 0), so no
  // bottom-extension hack is needed — the content naturally fills the clip.
  const parallaxOffset =
    hasFlexibleSpace && appBarOptions?.flexibleSpace?.parallax !== false
      ? Math.min(scrollY, collapseRange) * 0.55
      : 0;

  // Appbar background alpha: blends from ~10% opacity (expanded) to ~50% (collapsed).
  // 50% matches TopBar's `${theme.background}80` convention. Assumes theme colors are 6-digit hex.
  const appBarBgAlphaHex = Math.round((0.1 + 0.4 * collapseProgress) * 255)
    .toString(16)
    .padStart(2, '0');

  // ── Items (optional addItemComponent prepended as sentinel) ──────────────────

  type ListItem = T | AddItemSentinel;

  const allItems = useMemo<ListItem[]>(() => {
    if (addItemComponent) return [ADD_ITEM_SENTINEL, ...items] as ListItem[];
    return items as ListItem[];
  }, [items, addItemComponent]);

  const renderListItem = useCallback(
    (item: ListItem, idx: number): ReactNode => {
      if (isAddItemSentinel(item)) return addItemComponent!;
      const actualIndex = addItemComponent ? idx - 1 : idx;
      return renderItem(item as T, actualIndex);
    },
    [addItemComponent, renderItem]
  );

  const getListItemKey = useCallback(
    (item: ListItem, idx: number): string | number => {
      if (isAddItemSentinel(item)) return '__flexlist_add_item__';
      return getItemKey(item as T, addItemComponent ? idx - 1 : idx);
    },
    [addItemComponent, getItemKey]
  );

  const showContent = allItems.length > 0;
  const showEmptyState = items.length === 0;

  // Wrap onVisibleItemsChange so callers always receive items of type T (never the add-item sentinel)
  // and indices adjusted to the original items array (no sentinel offset).
  const wrappedOnVisibleItemsChange = useMemo(() => {
    if (!onVisibleItemsChange) return undefined;
    const offset = addItemComponent ? 1 : 0;
    return (
      visibleItems: ListItem[],
      visibleStart: number,
      visibleEnd: number,
      renderedItems: ListItem[],
      renderedStart: number,
      renderedEnd: number,
    ) => {
      onVisibleItemsChange(
        visibleItems.filter((item): item is T => !isAddItemSentinel(item)),
        Math.max(0, visibleStart - offset),
        Math.max(0, visibleEnd - offset),
        renderedItems.filter((item): item is T => !isAddItemSentinel(item)),
        Math.max(0, renderedStart - offset),
        Math.max(0, renderedEnd - offset),
      );
    };
  }, [onVisibleItemsChange, addItemComponent]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      {...(qa ? qaAttr(qa) : {})}
      className={className}
      style={{ backgroundColor, minHeight: '100vh', ...style }}
    >
      {/* ── App Bar — sticky at the top level of the page div so it stays pinned
          throughout the full scroll (not inside the expandedHeight container, which
          was the old bug). Respects the parent container's max-width since sticky
          elements stay in the normal flow. Equivalent to SliverAppBar(pinned:true). */}
      {appBarOptions && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            // iOS PWA: extend the appbar behind the status bar and push content
            // below it via paddingTop. The blur backdrop fills the full area
            // (status bar included), so the visual is one continuous bar.
            height: `calc(${titleBarHeight}px + ${responsive.safeArea.top})`,
            paddingTop: responsive.safeArea.top,
            zIndex: 50,
          }}
        >
          {/* Blur backdrop — always present like TopBar; opacity scales from ~10%
              when the flexible space is fully expanded to ~50% when fully collapsed. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor:
                (appBarOptions.backgroundColor ?? theme.background) + appBarBgAlphaHex,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          />

          {/* Title bar content */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              height: titleBarHeight,
              paddingLeft: responsive.spacing.sm,
              paddingRight: responsive.spacing.sm,
              gap: responsive.gap.sm,
            }}
          >
            {/* Leading */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
              {appBarOptions.hideLeading
                ? null
                : (appBarOptions.leadingWidget ?? (
                  <BackIconButton onClick={appBarOptions.onLeadingTap} />
                ))}
            </div>

            {/* Title — fades in when the flexible space collapses */}
            <div
              style={{
                flexShrink: 1,
                textAlign: 'center',
                fontWeight: responsive.fontWeight.bold,
                fontSize: responsive.font.xl,
                color: theme.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                // When a flexible space is present, the title fades in as it collapses
                // (mirroring Flutter's SliverAppBar title visibility behavior).
                // `alwaysShowTitle` opts out of the fade and keeps the title fully visible.
                opacity: hasFlexibleSpace && !appBarOptions.alwaysShowTitle ? collapseProgress : 1,
                transition: 'opacity 0.15s',
              }}
            >
              {appBarOptions.title}
              {appBarOptions.titleExtra}
            </div>

            {/* Actions */}
            <div style={{
              flex: 1, display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
              // Same padding set for the backbutton on the left
              paddingRight: responsive.spacing.sm
            }}>
              {appBarOptions.actions}
            </div>
          </div>
        </div>
      )}

      {/* ── Flexible space background — pulls up behind the sticky appbar via
          negative margin so the full expandedHeight is used (appbar overlaps
          the top titleBarHeight px). Content inside the flexible space should
          use paddingTop: titleBarHeight to clear the appbar visually. ──────── */}
      {hasFlexibleSpace && (
        <div
          style={{
            // Outer clip: once measured, locks to the measured height so
            // overflow:hidden trims the parallax-translated content correctly.
            // position:relative is required for the scroll-driven overlay inside.
            position: 'relative',
            height: measuredExpandedHeight > 0 ? measuredExpandedHeight : 'auto',
            // Pull up by the full appbar height (title bar + iOS PWA safe area)
            // so the flexibleSpace overlaps the appbar exactly.
            marginTop: `calc(-${titleBarHeight}px - ${responsive.safeArea.top})`,
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          {/* Ref is on the INNER div so ResizeObserver tracks content height,
              not the outer clip div. The outer clip has an explicit height once
              measured, so its own size never changes when async content loads
              (e.g. user data arriving). Observing the inner div correctly
              detects those changes and keeps measuredExpandedHeight in sync. */}
          <div
            ref={flexibleSpaceRef}
            style={{
              width: '100%',
              transform: `translateY(${parallaxOffset}px)`,
            }}
          >
            {/* Transparent spacer matching the overlapping sticky appbar height
                (title bar + iOS PWA safe area). The safe area must be folded into
                the spacer's own height — using `marginTop` instead lets margin
                collapse into the parent, which causes the ResizeObserver-measured
                inner-div height to under-report by `safeArea.top` and the outer
                clip's `overflow: hidden` ends up clipping the bottomPadding. */}
            <div style={{ height: `calc(${titleBarHeight}px + ${responsive.safeArea.top})` }} />
            <div style={{ height: appBarOptions!.flexibleSpace!.topPadding ?? responsive.gap.lg }} />
            {appBarOptions!.flexibleSpace!.background ?? (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: responsive.spacing['2xl'],
                  paddingBottom: responsive.spacing['2xl'],
                  ...appBarOptions!.flexibleSpace!.backgroundStyle,
                }}
              >
                {appBarOptions!.flexibleSpace!.centerWidget && (
                  <div
                    style={{
                      width: appBarOptions!.flexibleSpace!.centerWidgetSize ?? 80,
                      height: appBarOptions!.flexibleSpace!.centerWidgetSize ?? 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {appBarOptions!.flexibleSpace!.centerWidget}
                  </div>
                )}
              </div>
            )}
            <div style={{ height: appBarOptions!.flexibleSpace!.bottomPadding ?? responsive.gap['2xl'] }} />
          </div>

          {/* Scroll-driven overlay: darkens the flexible space as the user scrolls.
              pointer-events:none lets all click/touch events pass through to the
              content beneath (e.g. Subscribe / Message buttons). */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: theme.backgroundSecondary,
              opacity: collapseProgress,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        </div>
      )}

      {/* ── Persistent header ────────────────────────────────────────────────── */}
      {persistentHeader && (
        <div
          style={{
            position: 'sticky',
            // Stick directly below the appbar (title bar + iOS PWA safe area).
            top: `calc(${titleBarHeight}px + ${responsive.safeArea.top})`,
            height: persistentHeaderHeight,
            zIndex: 40,
            backgroundColor: theme.background,
          }}
        >
          {persistentHeader}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      <div>
        {/* VirtualList */}
        {showContent && (
          <VirtualList
            ref={virtualListRef}
            items={allItems}
            renderItem={renderListItem}
            getItemKey={getListItemKey}
            {...(listQa ? { qa: listQa } : {})}
            {...(gridProps !== undefined ? { gridProps } : {})}
            {...(initialIndex !== undefined ? { initialIndex } : {})}
            {...(reversed ? { reversed } : {})}
            {...(wrappedOnVisibleItemsChange ? { onVisibleItemsChange: wrappedOnVisibleItemsChange } : {})}
            {...(hasMore && onLoadMore ? { onReachedEnd: onLoadMore, reachedEndThreshold: 200 } : {})}
          />
        )}

        {/* Empty state — shown when items is empty, matching Flutter's emptyListWidget behavior.
            Displayed alongside the grid (which may still show the addItemComponent slot). */}
        {showEmptyState && (
          errorOnFirstFetchComponent ?? emptyListComponent ?? (
            <div
              style={{
                textAlign: 'center',
                paddingTop: responsive.spacing['3xl'],
                paddingBottom: responsive.spacing['3xl'],
                paddingLeft: responsive.spacing.lg,
                paddingRight: responsive.spacing.lg,
                color: theme.text.secondary,
              }}
            >
              {l10n.common.noData}
            </div>
          )
        )}

        {/* Loading indicator while a load-more request is in flight */}
        {hasMore && isLoadingMore && items.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: responsive.spacing.lg }}>
            <ExpauseLoadingIndicator />
          </div>
        )}
      </div>
    </div>
  );
}) as <T>(
  props: FlexibleHeaderListProps<T> & { ref?: React.Ref<FlexibleHeaderListHandle> }
) => React.ReactElement;
